"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChangePlanDialog } from "@/components/dashboard/change-plan-dialog";
import { useAuthStore } from "@/lib/stores/auth-store";

/**
 * Non-blocking banner shown to users with Plan.Expired. The dashboard stays
 * fully navigable; only signal processing is paused (inbound signals are
 * rejected server-side in SignalIngressService until the user reactivates).
 */
export function ReactivationBanner() {
  const t = useTranslations();
  const { user, loadUser } = useAuthStore();
  const [pickerOpen, setPickerOpen] = useState(false);

  if (user?.plan?.toLowerCase() !== "expired") return null;

  return (
    <div
      role="note"
      className="mb-4 flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4 sm:flex-row sm:items-center"
    >
      <AlertTriangle
        aria-hidden="true"
        className="h-5 w-5 shrink-0 text-warning"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">
          {t("dashboard.reactivation.bannerTitle")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("dashboard.reactivation.bannerBody")}
        </p>
      </div>
      {/* Opens the picker in place. This used to link to /?upgrade=1#pricing,
          which threw an expired customer out of the dashboard and onto the
          marketing page, where the copy offers a 14-day free trial with no
          credit card to someone whose paid subscription has just lapsed. */}
      <Button size="sm" className="shrink-0" onClick={() => setPickerOpen(true)}>
        {t("dashboard.reactivation.reactivate")}
      </Button>

      <ChangePlanDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        currentPlanCode="expired"
        // An expired account has nothing for Stripe to modify, so reactivating
        // is a Checkout regardless of what subscription id may be left over.
        hasSubscription={false}
        onChanged={() => loadUser()}
      />
    </div>
  );
}
